import { logRBIEvent } from 'features/analytics';
import { AllowedEvent } from 'features/analytics/allowed-events';
import { CustomEventNames, EventTypes } from 'features/analytics/constants';
import { IAmplitudeProduct } from 'features/analytics/types';
import {
  convertToSingleCommerceProduct,
  createProduct,
  getCartDataItems,
} from 'features/cart/utils/analytics';
import logger from 'features/observability/logger';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { serializePickupMode } from 'state/service-mode/serializers';
import { ServiceMode } from 'state/service-mode/types';

/**
 * Checkout "Change" button analytics distinguish the section being edited while preserving the
 * shared Button Click event shape.
 */
export const AMPLITUDE_CHECKOUT_SECTION_PROPERTY = 'Checkout Section';

export const AmplitudeCheckoutSection = {
  Restaurant: 'Restaurant',
  PaymentMethod: 'Payment Method',
  ServiceMode: 'Service Mode',
} as const;

export type AmplitudeCheckoutSectionValue = typeof AmplitudeCheckoutSection[keyof typeof AmplitudeCheckoutSection];

export type CheckoutChangeButtonEventAttributes = {
  [AMPLITUDE_CHECKOUT_SECTION_PROPERTY]: AmplitudeCheckoutSectionValue;
};

export const getCheckoutChangeButtonEventAttributes = (
  section: AmplitudeCheckoutSectionValue
): CheckoutChangeButtonEventAttributes => ({
  [AMPLITUDE_CHECKOUT_SECTION_PROPERTY]: section,
});

export const buildCheckoutEvents = ({
  serviceMode,
  cartEntries,
  shouldLogIndividualProducts = false,
}: {
  serviceMode: ServiceMode;
  cartEntries: ILegacyCartEntry[];
  shouldLogIndividualProducts?: boolean;
}) => {
  const products = cartEntries.map(createProduct).filter(Boolean) as IAmplitudeProduct[];
  const customAttributes = {
    'Pickup Mode': serializePickupMode(serviceMode),
    'Cart Data': getCartDataItems(cartEntries),
    'Has Upsell': cartEntries.some(entry => entry.isUpsell),
    'Has Donation': cartEntries.some(entry => entry.isDonation),
    'Has Offer': cartEntries.some(entry => entry.type.includes('Offer')),
    'Has Extra': cartEntries.some(entry => entry.isExtra),
    'Total Amount': products.reduce((acc, curr) => acc + curr.total_product_amount, 0),
    'Product Count': products.length,
  };

  return [
    {
      name: CustomEventNames.E_COMMERCE_CHECKOUT,
      type: EventTypes.Other,
      attributes: { ...customAttributes, products },
    } as AllowedEvent,
    ...(shouldLogIndividualProducts
      ? (products.map(product => ({
          name: 'eCommerce - Checkout - Item',
          type: EventTypes.Other,
          attributes: { ...customAttributes, ...convertToSingleCommerceProduct(product) },
        })) as AllowedEvent[])
      : []),
  ];
};

export const logCheckoutEvent = (options: Parameters<typeof buildCheckoutEvents>[0]) => {
  try {
    buildCheckoutEvents(options).forEach(event => logRBIEvent(event));
  } catch (error) {
    logger.error({ error, message: 'Error logging CRM eCommerce - Checkout event' });
  }
};
